import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { Web3WalletSignatureInfo, Web3WalletType } from '../../interfaces/auth.interface';
import { NetworksInterface } from '../../interfaces/deposit.interface';
import { Web3WalletAdapter } from '../../interfaces/web3-wallet.interface';
import { LocalStorageService } from '../localstorage.service';
import { CoinbaseWalletService } from './coinbase-wallet.service';
import { MetaMaskService } from './metamask.service';
import { TonConnectService } from './ton-connect.service';
import { WalletConnectService } from './wallet-connect.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class Web3WalletService implements Web3WalletAdapter, OnDestroy {
  constructor(
    // private metaMaskSDKService: MetaMaskSDKService,
    private metaMaskService: MetaMaskService,
    private coinbaseWalletService: CoinbaseWalletService,
    private walletConnectService: WalletConnectService,
    private tonConnectService: TonConnectService,
    private localStorageService: LocalStorageService,
  ) {
    // 恢復 Web3 當前連線(處理因 login/logout 而 window.reBuild 導致 angular 重新載入問題)
    this.checkExistingConnection();
  }

  private _currentWalletService: Web3WalletAdapter | null = null;
  private readonly _destroyCurrSubscription$ = new Subject<void>();
  private readonly _walletServiceMap: Partial<Record<Web3WalletType, Web3WalletAdapter>> = {
    [Web3WalletType.MetaMask]: this.metaMaskService,
    [Web3WalletType.CoinbaseWallet]: this.coinbaseWalletService,
    [Web3WalletType.TonConnect]: this.tonConnectService,
    [Web3WalletType.WalletConnect]: this.walletConnectService,
  };

  private readonly _isConnected$ = new BehaviorSubject<boolean>(false);
  private readonly _walletType$ = new BehaviorSubject<Web3WalletType | null>(null);
  private readonly _accounts$ = new BehaviorSubject<string[]>([]);
  private readonly _currentChainId$ = new BehaviorSubject<number | string | null>(null);

  readonly isConnected$: Observable<boolean> = this._isConnected$.asObservable();
  readonly walletType$: Observable<Web3WalletType | null> = this._walletType$.asObservable();
  readonly accounts$: Observable<string[]> = this._accounts$.asObservable();
  readonly currentChainId$: Observable<number | string | null> = this._currentChainId$.asObservable();

  ngOnDestroy(): void {
    this._destroyCurrSubscription$.next();
    this._destroyCurrSubscription$.complete();
  }

  /**
   * 重置狀態
   */
  private _resetWalletState(): void {
    // Remove the wallet type from localStorage
    this.localStorageService.clearConnectedWeb3WalletType();

    this._isConnected$.next(false);
    this._walletType$.next(null);
    this._accounts$.next([]);
    this._currentChainId$.next(null);
    this._currentWalletService = null;
  }

  /**
   * 確認 Web3WalletType Service 是否存在
   */
  private _checkWalletServiceExisting(): void {
    if (!this._currentWalletService) {
      throw new Error('No web3 wallet service available');
    }
  }

  /**
   * 初始化 Web3WalletType Service 與相關訂閱設置
   * @param walletType
   */
  private async _walletServiceInit(walletType: Web3WalletType): Promise<void> {
    try {
      // 清理之前的訂閱
      if (this._currentWalletService || this._walletType$.getValue()) {
        this._destroyCurrSubscription$.next();
        this._resetWalletState();
      }

      // Store the wallet type in localStorage
      this.localStorageService.setConnectedWeb3WalletType(walletType);

      // 選擇對應的錢包服務
      this._currentWalletService = this._walletServiceMap[walletType] || null;
      this._checkWalletServiceExisting();

      // 設置新的連線類型
      this._walletType$.next(walletType);

      // 設置新的訂閱
      this._setupWalletStateSubscription();
    } catch (error) {
      this._destroyCurrSubscription$.next();
      this._resetWalletState();
      throw error;
    }
  }

  /**
   * 訂閱 Web3WalletType Service 的狀態
   */
  private _setupWalletStateSubscription(): void {
    if (!this._currentWalletService) return;

    // 訂閱連接狀態
    this._currentWalletService.isConnected$
      .pipe(untilDestroyed(this), takeUntil(this._destroyCurrSubscription$))
      .subscribe(isConnected => {
        this._isConnected$.next(isConnected);
      });

    // 訂閱帳戶變化
    this._currentWalletService.accounts$
      .pipe(untilDestroyed(this), takeUntil(this._destroyCurrSubscription$))
      .subscribe(accounts => {
        this._accounts$.next(accounts);
      });

    // 訂閱鏈ID變化
    this._currentWalletService.currentChainId$
      .pipe(untilDestroyed(this), takeUntil(this._destroyCurrSubscription$))
      .subscribe(chainId => {
        this._currentChainId$.next(chainId);
      });
  }

  async checkExistingConnection(): Promise<boolean> {
    const currWalletType = this.localStorageService.getConnectedWeb3WalletType();
    if (currWalletType && Object.values(Web3WalletType).includes(currWalletType)) {
      await this._walletServiceInit(currWalletType);
      return true;
    }

    return false; // 未符合時返回 false
  }

  async connectWallet(walletType: Web3WalletType): Promise<void> {
    await this._walletServiceInit(walletType);

    // 實際執行連接
    this._checkWalletServiceExisting();
    return this._currentWalletService!.connectWallet(walletType);
  }

  async disconnectWallet(): Promise<void> {
    this._destroyCurrSubscription$.next();
    this._resetWalletState();
    for (const service of Object.values(this._walletServiceMap)) {
      await service.disconnectWallet();
    }
  }

  updateConnectedStatus(isConnected: boolean): void {
    if (!this._currentWalletService) {
      this._isConnected$.next(isConnected);
    } else {
      this._currentWalletService.updateConnectedStatus(isConnected);
    }
  }

  async signMessage(message: string): Promise<Web3WalletSignatureInfo | null> {
    this._checkWalletServiceExisting();

    // 如果連線後，簽名沒有成功。統一取消連線，確保可以切換錢包後，再次連線簽名
    try {
      const result = await this._currentWalletService!.signMessage(message);
      return result;
    } catch {
      await this.disconnectWallet();
      return null;
    }
  }

  async fetchCurrentChainId(): Promise<number | string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.fetchCurrentChainId();
  }

  async fetchBalance(): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.fetchBalance();
  }

  async fetchContractBalance(network: NetworksInterface): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.fetchContractBalance(network);
  }

  async sendMainTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.sendMainTransaction(toAddress, amount, network);
  }

  async sendContractTransaction(toAddress: string, amount: string, network: NetworksInterface): Promise<string | null> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.sendContractTransaction(toAddress, amount, network);
  }

  async switchNetwork(chainId: number | string | null): Promise<void> {
    this._checkWalletServiceExisting();
    return this._currentWalletService!.switchNetwork(chainId);
  }
}
